import React, { useEffect, useRef, useState } from "react";
import supabase from "../config/supabaseConfig";
import "../styles/ApontamentoVolume.css";

// Apontamento de volume por grupamento (patch 2026) — layout de
// docs/prototipos/sala.html. Reunião 12/06 (Alex): stepper +/− por
// grupamento, cor vs meta do aluno (abaixo=amarelo, no ideal=verde,
// acima=vermelho — fica vermelho como alerta de volume estourado).
//
// Fase 3 (10/07): persiste em `aluno_volume` (upsert aluno_id+grupamento,
// sigla oficial como chave). O estado local (useVolumePorAluno) continua
// sendo a fonte imediata da UI — o write no banco é debounced por
// aluno+grupamento pra não disparar um upsert a cada clique.

// 10 grupamentos validados + meta-referência default (mesmos defaults de
// CargasAluno.jsx — QD e PC batem com o exemplo real do Alex na reunião).
// O default só vale enquanto o aluno não tem `meta` gravada em aluno_volume.
const GRUPAMENTOS = [
  { sigla: "QD", nome: "Quadríceps", alvo: 6 },
  { sigla: "PC", nome: "Posterior de coxa", alvo: 10 },
  { sigla: "PT", nome: "Panturrilha", alvo: 6 },
  { sigla: "A", nome: "Adutores", alvo: 6 },
  { sigla: "G", nome: "Glúteo", alvo: 6 },
  { sigla: "P", nome: "Peitoral", alvo: 6 },
  { sigla: "D", nome: "Dorsal", alvo: 6 },
  { sigla: "O", nome: "Ombro", alvo: 6 },
  { sigla: "B", nome: "Bíceps", alvo: 6 },
  { sigla: "T", nome: "Tríceps", alvo: 6 },
];

const metaDefault = (sigla) =>
  GRUPAMENTOS.find((g) => g.sigla === sigla)?.alvo ?? 6;

const statusDe = (v, alvo) => (v < alvo ? "low" : v === alvo ? "ok" : "high");
const labelDe = (v, alvo) =>
  v < alvo ? "Abaixo" : v === alvo ? "No ideal" : "Acima";

// `metas` (opcional): overrides de meta persistida por sigla — quando
// ausente, usa o default de referência (GRUPAMENTOS[].alvo).
const ApontamentoVolume = ({ valores, metas, onChange }) => {
  const get = (sigla) => valores?.[sigla] ?? 0;
  const getMeta = (sigla) => metas?.[sigla] ?? metaDefault(sigla);
  // onChange recebe o DELTA (não o valor absoluto): em toque duplo/clique
  // rápido (tablet), ler "valor atual" do snapshot de render e somar aqui
  // perderia incrementos — quem aplica o delta usa setState funcional
  // (useVolumePorAluno.bumpValor), sempre a partir do estado mais recente.
  const alterar = (sigla, delta) => onChange(sigla, delta);

  return (
    <div className="apv-grid">
      {GRUPAMENTOS.map((g) => {
        const v = get(g.sigla);
        const alvo = getMeta(g.sigla);
        const status = statusDe(v, alvo);
        return (
          <div key={g.sigla} className={`apv-card apv-${status}`}>
            <div className="apv-top">
              <span className="apv-sigla">{g.sigla}</span>
              <span className="apv-status">{labelDe(v, alvo)}</span>
            </div>
            <div className="apv-nome">
              {g.nome} · ideal {alvo} séries
            </div>
            <div className="apv-counter-row">
              <div className={`apv-counter apv-counter-${status}`}>
                <button
                  type="button"
                  onClick={() => alterar(g.sigla, -1)}
                  aria-label={`Diminuir ${g.nome}`}
                >
                  −
                </button>
                <span className="apv-val">{v}</span>
                <button
                  type="button"
                  onClick={() => alterar(g.sigla, 1)}
                  aria-label={`Aumentar ${g.nome}`}
                >
                  +
                </button>
              </div>
              <span className="apv-meta">/ {alvo}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Debounce por chave (aluno+grupamento) antes de gravar no banco — evita
// 1 upsert por clique em sequências rápidas de +/-.
const DEBOUNCE_MS = 500;

// Hook pra gerenciar o estado de volume por aluno, com persistência real em
// `aluno_volume` (Fase 3, 10/07). `alunoIds`: lista dos alunos que devem ter
// o volume carregado do banco (ex.: alunos em aula agora) — cada id só é
// buscado uma vez (cache local via ref), novos ids entram sob demanda.
//
// O estado local (porAluno) continua sendo a fonte imediata da UI: bumpValor
// aplica o DELTA via setState funcional — seguro mesmo com cliques/toques
// rápidos em sequência (tablet), que já não pegariam o valor mais recente se
// lêssemos de uma prop/closure. O write no banco é agendado (debounce) e lê
// o valor mais recente de uma ref espelhada do estado (valoresRef), não do
// fechamento do clique — assim não perde incrementos nem grava valor velho.
//
// Uso: const { getValores, getMetas, bumpValor } = useVolumePorAluno(ids);
// <ApontamentoVolume valores={getValores(alunoId)} metas={getMetas(alunoId)}
// onChange={(sigla, delta) => bumpValor(alunoId, sigla, delta)} />
export const useVolumePorAluno = (alunoIds = []) => {
  const [porAluno, setPorAluno] = useState({});
  const [metaPorAluno, setMetaPorAluno] = useState({});
  const valoresRef = useRef({});
  const metasRef = useRef({});
  const carregadosRef = useRef(new Set());
  const timersRef = useRef({});

  useEffect(() => {
    valoresRef.current = porAluno;
  }, [porAluno]);
  useEffect(() => {
    metasRef.current = metaPorAluno;
  }, [metaPorAluno]);

  // Chave estável pro effect de carga — evita refetch a cada render quando
  // o chamador passa um array novo (ex.: alunosEmAula.map(a => a.id)).
  const idsKey = (alunoIds || []).filter(Boolean).join(",");

  useEffect(() => {
    const ids = idsKey ? idsKey.split(",") : [];
    const novos = ids.filter((id) => !carregadosRef.current.has(id));
    if (novos.length === 0) return;
    novos.forEach((id) => carregadosRef.current.add(id));

    // SEM guard de "ativo"/cleanup aqui: com StrictMode (dev) o effect roda
    // 2x — descartar a 1ª busca deixava os ids marcados em carregadosRef sem
    // dado nenhum (a 2ª rodada pula por causa do cache) e o próximo
    // apontamento sobrescreveria o valor real no banco. O merge abaixo é
    // idempotente (chaveado por aluno+grupamento), então aplicar uma busca
    // "atrasada" é sempre seguro; setState pós-unmount é no-op no React 18.
    (async () => {
      const { data, error } = await supabase
        .from("aluno_volume")
        .select("aluno_id, grupamento, series_semana, meta")
        .in("aluno_id", novos);
      if (error) {
        console.error("Erro ao carregar aluno_volume:", error);
        // permite retry na próxima mudança de idsKey — senão o aluno fica
        // marcado como carregado com estado vazio (risco de sobrescrita)
        novos.forEach((id) => carregadosRef.current.delete(id));
        return;
      }
      if (!Array.isArray(data)) return;

      setPorAluno((prev) => {
        const next = { ...prev };
        data.forEach((row) => {
          next[row.aluno_id] = {
            ...next[row.aluno_id],
            [row.grupamento]: row.series_semana ?? 0,
          };
        });
        return next;
      });
      setMetaPorAluno((prev) => {
        const next = { ...prev };
        data.forEach((row) => {
          if (row.meta != null) {
            next[row.aluno_id] = {
              ...next[row.aluno_id],
              [row.grupamento]: row.meta,
            };
          }
        });
        return next;
      });
    })();
  }, [idsKey]);

  const agendarSave = (alunoId, sigla) => {
    const key = `${alunoId}:${sigla}`;
    clearTimeout(timersRef.current[key]);
    timersRef.current[key] = setTimeout(async () => {
      const seriesSemana = valoresRef.current[alunoId]?.[sigla] ?? 0;
      const meta = metasRef.current[alunoId]?.[sigla] ?? metaDefault(sigla);
      const { error } = await supabase
        .from("aluno_volume")
        .upsert(
          {
            aluno_id: alunoId,
            grupamento: sigla,
            series_semana: seriesSemana,
            meta,
          },
          { onConflict: "aluno_id,grupamento" }
        );
      if (error) {
        console.error("Erro ao salvar aluno_volume:", error);
      }
    }, DEBOUNCE_MS);
  };

  const getValores = (alunoId) => porAluno[alunoId] || {};
  const getMetas = (alunoId) => metaPorAluno[alunoId] || {};
  const bumpValor = (alunoId, sigla, delta) => {
    setPorAluno((prev) => {
      const atual = prev[alunoId]?.[sigla] ?? 0;
      const novo = Math.max(0, atual + delta);
      return { ...prev, [alunoId]: { ...prev[alunoId], [sigla]: novo } };
    });
    agendarSave(alunoId, sigla);
  };

  return { getValores, getMetas, bumpValor };
};

export default ApontamentoVolume;
